/**
 * ================================================================================================
 *  התאמה אישית — WHICH CARDS APPEAR ON THE HOME SCREEN, AND A WAY BACK
 * ================================================================================================
 *
 * **Owner, 2026-11-01, the last line of the home-screen list:** *"העבר את ההתאמה האישית מדף הבית
 * אל ההגדרות."*
 *
 * ── WHY THIS IS A SCREEN AND NOT A PANEL ON THE HOME SCREEN ANY MORE ──────────────────────────
 *
 * The panel was a `<details>` above both regions, closed on arrival, and the argument for putting
 * it there was the Owner's own sentence from section 4 of the same round: *"אין להחזיר כרטיסי
 * ניהול מיותרים למרכז דף הבית"* — the control for choosing cards must not itself become a card
 * competing with the work. A closed disclosure was the smallest thing that could satisfy that.
 *
 * It is still one line of chrome, and one line of chrome at the top of the work area is one line
 * the work does not get. Settings is where a person goes to configure the office; the arrangement
 * of their own home screen is the same kind of act, and the landing at `/tenant` is a grid of
 * categories built exactly for it.
 *
 * ── WHAT IT CHANGES ABOUT THE STATE, WHICH IS LESS THAN IT LOOKS ──────────────────────────────
 *
 * The home screen holds the ORDER in React state because a drag has to move a card under the
 * pointer. Nothing on this screen is dragged, so this screen holds only the hidden set — and the
 * reset writes all three preferences through `resetHome()`, exactly as the panel always did.
 *
 * **AND THE HOME SCREEN DOES NOT NEED TELLING.** Every link in this product is a page load
 * (`BrowserRouter` is mounted and the navigation is plain `<a href>`), so a person arriving at
 * `/home` after changing something here mounts the home screen fresh, and it reads the order and
 * the hidden set from `homeOrder.ts` on the way up. A notification channel between two screens
 * that cannot be on screen at the same time would be a mechanism with no case to serve.
 *
 * ── THE ACCOUNT IS ASKED ONCE, ON MOUNT ───────────────────────────────────────────────────────
 *
 * The same effect the home screen runs, for the same reason: the first paint is this machine's
 * answer and the account's arrives a moment later. Without it, a person who hid a card on their
 * office machine would open this screen at home and see every box ticked — and ticking one would
 * write that wrong answer back over the account.
 */

import { useCallback, useEffect, useState } from 'react';

import { AppShell } from '../shell/AppShell';
import { Icon } from '../shell/Icon';
import { FINANCIAL_MODULES_FOR_HOME } from '../shell/destinations';
import { useFavourites } from '../shell/favourites';
import {
  chosenCurrencies,
  currenciesChangedOnAccount,
  DEFAULT_CURRENCIES,
  setCurrencyShown,
} from '../shell/currencies';
import { useWorkContext } from '../shell/ContextProvider';
import { useLocale } from '../i18n/LocaleProvider';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { hydratePreferences } from '../shell/preferences';
import { favouritesChangedOnAccount } from '../shell/favourites';
import {
  DEFAULT_MAIN_ORDER,
  DEFAULT_SIDE_ORDER,
  hiddenTiles,
  resetHome,
  setTileShown,
  TILE_LABEL,
  type HomeTileId,
} from '../shell/homeOrder';
import { routes } from '../routes';
import type { TextKey } from '../i18n/catalogue';

/**
 * THE CURRENCY NAMES, BY CODE. The same keys `MarketTile` renders from — named here rather than
 * imported from a component, because a screen importing another screen's private table is how a
 * component becomes a library by accident. The CATALOGUE is the shared thing, and it is shared.
 */
const CURRENCY_LABEL: Readonly<Record<string, TextKey>> = {
  USD: 'market.currency.usd',
  EUR: 'market.currency.eur',
  GBP: 'market.currency.gbp',
  JPY: 'market.currency.jpy',
};

export function TenantPersonalisationScreen(): JSX.Element {
  const ctx = useWorkContext();
  const { t } = useLocale();
  const [hidden, setHidden] = useState<readonly string[]>(() => hiddenTiles());
  const { favourites, toggle } = useFavourites();
  const [currencies, setCurrencies] = useState<readonly string[]>(() => chosenCurrencies());

  useEffect(() => {
    let live = true;
    void hydratePreferences().then((changed) => {
      if (!live) return;
      if (changed.includes('home.hidden')) setHidden(hiddenTiles());
      /* The star travels on the account too, and `useFavourites` subscribes to this. */
      if (changed.includes('home.favourites')) favouritesChangedOnAccount();
      if (changed.includes('home.currencies')) {
        currenciesChangedOnAccount();
        setCurrencies(chosenCurrencies());
      }
    });
    return () => {
      live = false;
    };
  }, []);

  /** Switch one card on or off, and keep the screen and the preference in step. */
  const showCard = useCallback((id: HomeTileId, shown: boolean): void => {
    setHidden(setTileShown(id, shown));
  }, []);

  /*
   * BACK TO THE APPROVED SCREEN. `resetHome()` writes three empty preferences — which IS the
   * approved arrangement, because `homeOrder()` reconciles an empty stored order against the
   * default and an empty hidden set is every card shown. The hidden set is then re-READ rather
   * than assigned, so this screen never becomes a second statement of what the default is.
   */
  const resetLayout = useCallback((): void => {
    resetHome();
    setHidden(hiddenTiles());
  }, []);

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('home.personalise')} requires="TENANT" actual={ctx.surface} />;
  }

  /*
   * ── THE REGIONS ARE LISTED SEPARATELY BECAUSE THE REGIONS ARE SEPARATE ──────────────────────
   *
   * A single list of seven would invite the reading that a card can be moved between the columns,
   * which it cannot: the two differ in width by design and a tile belongs to exactly one of them.
   * The lists are DERIVED from `DEFAULT_MAIN_ORDER` / `DEFAULT_SIDE_ORDER`, never restated here,
   * so a card added to a region appears here without anybody remembering to add it — and a card
   * RETIRED from a region stops being offered, which is what happened to three of them today.
   */
  const regions = [
    { key: 'main' as const, labelKey: 'home.mainRegion' as const, ids: DEFAULT_MAIN_ORDER },
    { key: 'side' as const, labelKey: 'home.sideRegion' as const, ids: DEFAULT_SIDE_ORDER },
  ];

  return (
    <AppShell
      translated
      titleHe={t('home.personalise')}
      screenId="tenant-personalisation"
      /*
        THE SETTINGS LANDING AND NOT THE HOME SCREEN, which is the convention every other
        category screen follows (`TenantOrganisationScreen`, `TenantReminderRulesScreen`): the
        parent of a category is the menu it was opened from. No lead line either — *"אני לא רוצה
        לראות הסברים בתוך המערכת"* — the card on the landing carries the one sentence.
      */
      backToParent={{ labelHe: t('settings.title'), href: routes.tenantAdmin }}
    >
      <section className="gp-panel gp-w-personalise">
        <div className="gp-w-personalise-body">
          {regions.map((region) => (
            <fieldset className="gp-w-personalise-region" key={region.key}>
              <legend>{t(region.labelKey)}</legend>
              {region.ids.map((id) => (
                <label className="gp-w-personalise-choice" key={id}>
                  <input
                    type="checkbox"
                    checked={!hidden.includes(id)}
                    onChange={(e) => showCard(id, e.target.checked)}
                  />
                  <span>{t(TILE_LABEL[id])}</span>
                </label>
              ))}
            </fieldset>
          ))}

          <p className="gp-w-personalise-actions">
            <button type="button" className="btn" onClick={resetLayout}>
              {t('home.resetLayout')}
            </button>
          </p>
          {/*
            ── AND IT SAYS, ON THE SCREEN, WHAT THE CHOICE IS NOT ────────────────────────────────

            *"הבחירה אינה מעניקה הרשאות או מנוי."* A person switching on a module's card is
            entitled to read that they changed a VIEW. The card itself still says `טרם מומש`;
            this says what the switch did.
          */}
          <p className="gp-muted gp-small">{t('home.personaliseScope')}</p>
        </div>
      </section>

      {/*
        ── THE MODULES, AND THE ONLY PLACE A STAR CAN BE SET. **Owner, 2026-11-01.** ───────────

            *"להוריד מועדפים, וכל המודולים שלי גם להוריד — לא צריך את זה, אלא אם סימן בכוכב את
            המודול הרלוונטי ואז הוא יוצג בדף הבית."*

        The list was a closed disclosure on the home screen whose entire content on arrival was
        its own name, and it went with the favourites panel. **Removing it removed the only
        control in this product that can SET a star**, which would have made the feature
        unreachable rather than quiet — so it is here, on the screen whose subject is which
        cards appear on a person's home screen. A star decides exactly that.

        IT IS NOT A DISCLOSURE HERE. On the home screen it was closed because eleven cards
        pushed the work below the fold; this screen is the place a person came to in order to
        choose, so the choices are visible when they arrive.
      */}
      {/*
        ── WHICH EXCHANGE RATES. **Owner, 2026-11-01.** *"אני רוצה לבחור איזה שער להוסיף."* ─────

        It was a constant in `MarketTile` — four, then two, then two in a different order — and
        each of those was me deciding in code what he is telling me is a choice. It is the fifth
        preference, on the same mechanism as the rest, and it belongs on this screen because
        this screen is where a person decides what their home screen carries.

        THE NAMES COME FROM THE SAME TABLE THE CARD USES. A second list of currency names here
        would be the duplicated vocabulary this repository keeps finding: `market.currency.*` is
        the one, and a code with no entry is offered by its code.
      */}
      <section className="gp-panel" aria-label={t('home.currencyChoice')}>
        <div className="gp-section-heading">
          <h2>{t('home.currencyChoice')}</h2>
        </div>
        <p className="gp-muted gp-small">{t('home.currencyChoiceHint')}</p>
        <fieldset className="gp-w-personalise-region">
          <legend>{t('home.widgets.fx')}</legend>
          {DEFAULT_CURRENCIES.map((code) => (
            /*
              ITS OWN CLASS, AND A CASE IS WHY. `gp-w-personalise-choice` is what the panel above
              uses for the home CARDS, and `personalisation.dom.test.tsx` counts those choices
              against the tile model — so four currency checkboxes wearing the same class made
              that count say eleven. A shared class between two lists that are counted separately
              is a measurement waiting to be wrong.
            */
            <label className="gp-w-currency-choice" key={code}>
              <input
                type="checkbox"
                checked={currencies.includes(code)}
                onChange={(e) => setCurrencies(setCurrencyShown(code, e.target.checked))}
              />
              <span>{CURRENCY_LABEL[code] === undefined ? code : t(CURRENCY_LABEL[code])}</span>
              <span className="gp-w-reading__code" dir="ltr">
                {code}
              </span>
            </label>
          ))}
        </fieldset>
      </section>

      <section className="gp-panel" aria-label={t('home.allModules')}>
        <div className="gp-section-heading">
          <h2>{t('home.allModules')}</h2>
          <span className="gp-badge gp-plan">{t('state.notImplemented')}</span>
        </div>
        <p className="gp-muted gp-small">{t('home.starsExplain')}</p>

        <div className="gp-w-modules">
          {FINANCIAL_MODULES_FOR_HOME.map((m) => {
            const starred = favourites.includes(m.id);
            return (
              <article className="gp-w-module" key={m.id}>
                <span className="gp-overview-icon">
                  <Icon name="chart-column" />
                </span>
                <h3>{t(m.label)}</h3>
                <span className="gp-badge gp-plan">{t('state.notImplemented')}</span>
                {/*
                  THE STAR WORKS ON A MODULE THAT DOES NOT, and the Owner confirmed that is
                  right: *"אפשר להשאיר כוכבית גם למודול עתידי — העדפה אישית יכולה לעבוד כבר
                  עכשיו, כל עוד כתוב בבירור שהמודול טרם מומש."* The chip beside it is what keeps
                  it from being an unexplained control.
                */}
                <button
                  type="button"
                  className="gp-w-star"
                  aria-pressed={starred}
                  aria-label={`${t(m.label)} — ${
                    starred ? t('nav.removeFavourite') : t('nav.addFavourite')
                  } (${t('state.notImplemented')})`}
                  onClick={() => toggle(m.id)}
                >
                  {starred ? '★' : '☆'}
                </button>
              </article>
            );
          })}
        </div>
      </section>
    </AppShell>
  );
}
